import React, { useRef, useState } from 'react';
import { Box, BoxProps, HStack, Input, Text, useBreakpointValue } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { FiChevronLeft, FiChevronRight, FiMinus, FiPlus } from 'react-icons/fi';
import { LuRotateCwSquare } from 'react-icons/lu';
import { PageNumberReasonChange } from '@/hooks/viewers/usePdfViewer.ts';
import { ZIndex } from '@/utils/zIndex.ts';

const DISABLED_CONTROL_PROPS = {
	cursor: 'default',
} as const;

interface PdfFloatingControlsProps {
	pageNumber: number;
	numPages: number;
	loading: boolean;
	onPrevPage: () => void;
	onNextPage: () => void;
	onPageChange: (page: number, reason: PageNumberReasonChange) => void;
	scale: number;
	onZoomIn: () => void;
	onZoomOut: () => void;
	onZoomReset: () => void;
	onRotateClockwise: () => void;
	left?: BoxProps['left'];
}

/**
 * Google Drive-like floating PDF controls positioned at bottom
 * Features numeric page input, zoom controls with icons, and navigation
 */
const PdfFloatingControls: React.FC<PdfFloatingControlsProps> = ({
	pageNumber,
	numPages,
	loading,
	onPrevPage,
	onNextPage,
	onPageChange,
	scale,
	onZoomIn,
	onZoomOut,
	onRotateClockwise,
	left = '50%',
}) => {
	const [pageInput, setPageInput] = useState(pageNumber.toString());
	const lastControlActionRef = useRef(false);
	const isMobile = useBreakpointValue({ base: true, md: false });

	// Only update input when it's NOT from a control action
	React.useEffect(() => {
		if (!lastControlActionRef.current) {
			setPageInput(pageNumber.toString());
		}
		// Reset the flag after the update cycle
		lastControlActionRef.current = false;
	}, [pageNumber]);

	const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		// Allow empty string and valid numbers only
		if (value === '' || /^\d+$/.test(value)) {
			setPageInput(value);
		}
	};

	const handlePrevClick = () => {
		lastControlActionRef.current = true;
		const newPage = Math.max(pageNumber - 1, 1);
		setPageInput(newPage.toString()); // Update input immediately
		onPrevPage();
	};

	const handleNextClick = () => {
		lastControlActionRef.current = true;
		const newPage = Math.min(pageNumber + 1, numPages);
		setPageInput(newPage.toString()); // Update input immediately
		onNextPage();
	};

	const handlePageInputSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// Handle empty input
		if (pageInput.trim() === '') {
			setPageInput(pageNumber.toString());
			return;
		}

		const newPage = parseInt(pageInput, 10);

		// Validate the parsed number
		if (!isNaN(newPage) && newPage >= 1 && newPage <= numPages) {
			lastControlActionRef.current = true;
			onPageChange(newPage, 'control');
		} else {
			// Reset to current page if invalid
			setPageInput(pageNumber.toString());
		}
	};

	const handlePageInputBlur = () => {
		handlePageInputSubmit({
			preventDefault: () => {},
		} as React.FormEvent);
	};

	return (
		<Box
			position="fixed"
			bottom={6}
			left={left}
			transform="translateX(-50%)"
			transition="left 180ms ease"
			zIndex={ZIndex.FileViewer}
			bg="blackAlpha.800"
			backdropFilter="blur(8px)"
			borderRadius="full"
			px={4}
			py={2}
			mb={4}
			boxShadow="lg"
			border="1px solid"
			borderColor="whiteAlpha.200"
		>
			<HStack gap={2} align="center">
				{/* Previous Page Button */}
				<IconButton
					size="sm"
					variant="ghost"
					color="white"
					_hover={{ bg: 'whiteAlpha.200' }}
					_disabled={DISABLED_CONTROL_PROPS}
					onClick={handlePrevClick}
					disabled={pageNumber <= 1 || loading}
					aria-label="Previous page"
				>
					<FiChevronLeft />
				</IconButton>

				{/* Page Input and Info */}
				<HStack gap={2} align="center" flexShrink={0}>
					<form onSubmit={handlePageInputSubmit}>
						<Input
							value={pageInput}
							onChange={handlePageInputChange}
							onBlur={handlePageInputBlur}
							size="sm"
							width="48px"
							textAlign="center"
							bg="whiteAlpha.200"
							border="1px solid"
							borderColor="whiteAlpha.300"
							color="white"
							borderRadius="md"
							_focus={{
								borderColor: 'primary',
								boxShadow: '0 0 0 1px var(--chakra-colors-primary)',
							}}
							_hover={{ bg: 'whiteAlpha.300' }}
						/>
					</form>
					{/* nowrap + no-shrink: this is the one flexible item, so under the bar's
					    shrink-to-fit width it would otherwise wrap "of N" onto a second line. */}
					<Text color="white" textStyle="label" whiteSpace="nowrap" flexShrink={0}>
						of {numPages}
					</Text>
				</HStack>

				{/* Next Page Button */}
				<IconButton
					size="sm"
					variant="ghost"
					color="white"
					_hover={{ bg: 'whiteAlpha.200' }}
					_disabled={DISABLED_CONTROL_PROPS}
					onClick={handleNextClick}
					disabled={pageNumber >= numPages || loading}
					aria-label="Next page"
				>
					<FiChevronRight />
				</IconButton>

				{/* Separator */}
				<Box w="1px" h="20px" bg="whiteAlpha.400" />

				{/* Zoom Out */}
				<IconButton
					size="sm"
					variant="ghost"
					color="white"
					_hover={{ bg: 'whiteAlpha.200' }}
					_disabled={DISABLED_CONTROL_PROPS}
					onClick={onZoomOut}
					disabled={loading}
					aria-label="Zoom out"
				>
					<FiMinus />
				</IconButton>

				{/* Zoom Level Display */}
				{!isMobile && (
					<Text color="white" textStyle="label.sm" minW="50px" textAlign="center">
						{Math.round(scale * 100)}%
					</Text>
				)}

				{/* Zoom In */}
				<IconButton
					size="sm"
					variant="ghost"
					color="white"
					_hover={{ bg: 'whiteAlpha.200' }}
					_disabled={DISABLED_CONTROL_PROPS}
					onClick={onZoomIn}
					disabled={loading}
					aria-label="Zoom in"
				>
					<FiPlus />
				</IconButton>

				{/* Separator */}
				<Box w="1px" h="20px" bg="whiteAlpha.400" />

				<IconButton
					size="sm"
					variant="ghost"
					color="white"
					_hover={{ bg: 'whiteAlpha.200' }}
					_disabled={DISABLED_CONTROL_PROPS}
					onClick={onRotateClockwise}
					disabled={loading}
					aria-label="Rotate clockwise"
				>
					<LuRotateCwSquare />
				</IconButton>

				{/*/!* Zoom Reset *!/*/}
				{/*<IconButton*/}
				{/*	size="sm"*/}
				{/**/}
				{/*	variant="ghost"*/}
				{/*	color="white"*/}
				{/*	_hover={{ bg: 'whiteAlpha.200' }}*/}
				{/*	onClick={onZoomReset}*/}
				{/*	disabled={loading}*/}
				{/*	aria-label="Reset zoom"*/}
				{/*>*/}
				{/*	<FiZoomIn />*/}
				{/*</IconButton>*/}
			</HStack>
		</Box>
	);
};

export default PdfFloatingControls;
